import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Ticket, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hw_token')}` });

const CAT_LABELS = { clima: 'Clima', impianti: 'Impianti', comfort: 'Comfort', dotazioni: 'Dotazioni', manutenzione: 'Manutenzione', comunicazioni: 'Comunicazioni' };

const Kpi = ({ testId, title, value, sub, valueClass = 'text-slate-800' }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testId}>
    <p className="text-xs text-slate-500 uppercase">{title}</p>
    <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
    {sub && <p className="text-xs text-slate-400">{sub}</p>}
  </div>
);

const slaClass = (pct) => {
  if (pct == null) return 'text-slate-300';
  if (pct >= 85) return 'text-emerald-600';
  if (pct >= 70) return 'text-amber-600';
  return 'text-rose-600';
};

export default function CsTicketsTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = new Date(Date.now() - (days - 1) * 864e5).toISOString().slice(0, 10);
      const to = new Date().toISOString().slice(0, 10);
      const r = await axios.get(`${API}/cs-control/tickets/metrics`,
        { params: { date_from: from, date_to: to }, headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento metriche ticket');
    }
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-500" /></div>;
  if (!data) return null;

  const t = data.tickets || {};
  const cmp = data.compare || {};
  const channels = ['calls', 'emails', 'tickets'];

  return (
    <div className="space-y-4" data-testid="cs-tickets-tab">
      <div className="flex flex-wrap items-center gap-2">
        {[7, 30, 90].map(d => (
          <button key={d} onClick={() => setDays(d)} data-testid={`cs-tk-days-${d}`}
            className={`px-3 py-1.5 rounded-full text-sm border ${days === d ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300'}`}>
            {d} giorni
          </button>
        ))}
      </div>

      {/* Canali a confronto */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="cs-channels-compare">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700">📊 Canali a confronto — Telefono / Email / Ticket</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Indicatore</th>
                <th className="text-center px-3 py-2">☎️ Telefono</th>
                <th className="text-center px-3 py-2">📧 Email</th>
                <th className="text-center px-3 py-2">🎫 Ticket</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-slate-100">
                <td className="px-4 py-2 text-slate-600">Volume in entrata</td>
                {channels.map(c => <td key={c} className="text-center px-3 py-2 font-bold">{cmp[c]?.volume ?? '—'}</td>)}
              </tr>
              <tr className="border-t border-slate-100">
                <td className="px-4 py-2 text-slate-600">Gestite (risposte / chiusi)</td>
                {channels.map(c => <td key={c} className="text-center px-3 py-2 font-bold">{cmp[c]?.handled ?? '—'}</td>)}
              </tr>
              <tr className="border-t border-slate-100">
                <td className="px-4 py-2 text-slate-600">Prima risposta</td>
                {channels.map(c => <td key={c} className="text-center px-3 py-2 font-semibold">{cmp[c]?.first_response ?? '—'}</td>)}
              </tr>
              <tr className="border-t border-slate-100">
                <td className="px-4 py-2 text-slate-600">% SLA</td>
                {channels.map(c => (
                  <td key={c} className={`text-center px-3 py-2 font-bold ${slaClass(cmp[c]?.sla_pct)}`}>
                    {cmp[c]?.sla_pct != null ? `${cmp[c].sla_pct}%` : '—'}
                    <span className="block text-[10px] font-normal text-slate-400">{cmp[c]?.sla_label}</span>
                  </td>
                ))}
              </tr>
              <tr className="border-t border-slate-100">
                <td className="px-4 py-2 text-slate-600">In sospeso</td>
                {channels.map(c => (
                  <td key={c} className="text-center px-3 py-2 font-semibold text-rose-600">
                    {cmp[c]?.pending ?? '—'}
                    <span className="block text-[10px] font-normal text-slate-400">{cmp[c]?.pending_label}</span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {t.backlog_open > 20 && (
        <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="cs-tk-backlog-warning">
          <AlertTriangle className="h-4 w-4" />
          Backlog elevato: <b>{t.backlog_open} ticket aperti</b> (età media {t.backlog_avg_age_days} gg, il più vecchio {t.backlog_oldest_days} gg)
        </div>
      )}

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi testId="cs-tk-opened" title="Aperti nel periodo" value={t.opened ?? 0} />
        <Kpi testId="cs-tk-closed" title="Chiusi nel periodo" value={t.closed ?? 0} />
        <Kpi testId="cs-tk-backlog" title="Backlog aperti" value={t.backlog_open ?? 0}
          sub={t.backlog_avg_age_days != null ? `età media ${t.backlog_avg_age_days} gg` : ''}
          valueClass={t.backlog_open > 20 ? 'text-rose-600' : 'text-slate-800'} />
        <Kpi testId="cs-tk-urgent" title="Urgenti aperti" value={t.backlog_urgent ?? 0}
          valueClass={t.backlog_urgent ? 'text-rose-600' : 'text-slate-800'} />
        <Kpi testId="cs-tk-first-reply" title="Prima risposta (mediana)"
          value={t.first_reply_median_hours != null ? `${t.first_reply_median_hours}h` : '—'} sub="SLA 24h" />
        <Kpi testId="cs-tk-sla" title="Entro 24h"
          value={t.within_sla_pct != null ? `${t.within_sla_pct}%` : '—'}
          valueClass={slaClass(t.within_sla_pct)} />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* Per categoria */}
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="cs-tk-categories">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Per categoria (aperti nel periodo)</h3>
          {Object.keys(t.by_category || {}).length === 0 ? <p className="text-sm text-slate-400">Nessun ticket nel periodo</p> : (
            <div className="space-y-1.5">
              {Object.entries(t.by_category).map(([cat, n]) => {
                const max = Math.max(...Object.values(t.by_category), 1);
                return (
                  <div key={cat} className="flex items-center gap-2 text-sm">
                    <span className="w-28 text-slate-600 capitalize">{CAT_LABELS[cat] || cat}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-3">
                      <div className="bg-indigo-500 h-3 rounded-full" style={{ width: `${(n / max) * 100}%` }} />
                    </div>
                    <span className="w-6 text-right font-semibold">{n}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {/* Priorità + chiusura */}
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="cs-tk-priority">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Priorità e chiusura</h3>
          <div className="space-y-2 text-sm">
            {Object.entries(t.by_priority || {}).map(([p, n]) => (
              <div key={p} className="flex justify-between">
                <span className={`capitalize ${p === 'urgente' ? 'text-rose-600 font-semibold' : 'text-slate-600'}`}>{p}</span>
                <b>{n}</b>
              </div>
            ))}
            <div className="border-t border-slate-100 pt-2 flex justify-between">
              <span className="text-slate-600">Tempo medio chiusura</span>
              <b>{t.closure_avg_days != null ? `${t.closure_avg_days} gg` : '—'}</b>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Messaggi scambiati</span>
              <b>{t.messages ?? 0}</b>
            </div>
          </div>
        </div>
        {/* Top immobili */}
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="cs-tk-properties">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Immobili con più ticket</h3>
          {(t.top_properties || []).length === 0 ? <p className="text-sm text-slate-400">—</p> : (
            <div className="space-y-1.5 text-sm">
              {t.top_properties.map(([name, n], i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-slate-600 truncate pr-2">{name}</span>
                  <b>{n}</b>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Operatori */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="cs-tk-operators">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <Ticket className="h-4 w-4 text-indigo-500" />
          <h3 className="text-sm font-semibold text-slate-700">Operatori ufficio sui ticket</h3>
        </div>
        {(t.operators || []).length === 0 ? (
          <p className="px-4 py-5 text-sm text-slate-400">Nessuna risposta ufficio nel periodo.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Operatore</th>
                <th className="text-right px-3 py-2">Messaggi inviati</th>
                <th className="text-right px-3 py-2">Ticket seguiti</th>
                <th className="text-right px-3 py-2">Prime risposte</th>
                <th className="text-right px-4 py-2">Mediana prima risposta</th>
              </tr>
            </thead>
            <tbody>
              {t.operators.map((o, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium text-slate-700">{o.name}</td>
                  <td className="text-right px-3 py-2">{o.messages}</td>
                  <td className="text-right px-3 py-2">{o.tickets}</td>
                  <td className="text-right px-3 py-2">{o.first_replies}</td>
                  <td className="text-right px-4 py-2">{o.first_reply_median_hours != null ? `${o.first_reply_median_hours}h` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
